import type { ReactNode } from "react";
import { useContext, useRef, useState } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import {
	useBookCarrier,
	useMutateCarriers,
	useSaveAdHocCarrier,
} from "~/features/quote/hooks/use-carrier-selection-hooks.ts";
import { useLtlActionErrorHandler } from "~/features/quote/hooks/use-ltl-action-error-handler.tsx";
import { useSubmitBookTenderShipment } from "~/features/quote/hooks/use-shipment-summary-hooks.tsx";
import { QUOTE_REFRESH_REQUIRED_STATUS } from "~/features/quote/utils/ltl-error-handler.ts";
import { QuoteStatus } from "~/shared/constants/quote-status.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { UpdateCarrierPriceBody } from "~/shared/schemas/carrier-price-schema.ts";
import type { CarrierPrice } from "~/shared/schemas/carrier-schema.ts";
import type {
	BookTenderResponse,
	QuoteResponse,
} from "~/shared/schemas/quote-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { ddBookCarrier, toDdWorkflow } from "~/shared/utils/datadog-actions.ts";
import { sendBookCarrierEvent } from "~/shared/utils/google-analytics-helper.ts";

// Track which ad-hoc carrierPrice ids have already been autosaved so we only
// perform the autosave once per id across component mounts.
const autosavedAdHocIds = new Set<number>();

/**
 * Hook to handle autosaving of carrier data with debouncing
 * @param carrierPrice - The carrier price object to autosave
 * @param isComplete - Whether the carrier data is complete
 * @param quoteId - The quote ID
 * @param eTag - The quote eTag
 * @returns Object containing saving state
 */
export function useCarrierAutosave(
	carrierPrice: CarrierPrice,
	isComplete: boolean,
	quoteId: number,
	eTag: string,
) {
	const [isSaving, setIsSaving] = useState(false);
	const { saveAdHocCarrier } = useSaveAdHocCarrier();

	const save = async () => {
		if (isComplete && quoteId > 0 && !autosavedAdHocIds.has(carrierPrice.id)) {
			autosavedAdHocIds.add(carrierPrice.id);
			setIsSaving(true);
			await saveAdHocCarrier(carrierPrice, quoteId, eTag);
			setIsSaving(false);
		}
	};

	return { isSaving, save };
}

export type UseCarrierBookingParams = {
	carrierPrice: CarrierPrice;
	onStepChange: () => void;
	onBookingStart?: () => void;
	onBookingEnd?: () => void;
	enableTenderOnBook?: boolean;
	onTenderSuccess?: (tenderedQuote: BookTenderResponse) => void;
	onTenderError?: (message: string) => void;
	onPaymentRequired?: (quote: QuoteResponse) => void;
};

export type UseCarrierBookingReturn = {
	handleBookCarrierClick: () => void;
	refreshModal: ReactNode;
	isRefreshing: boolean;
	isBooking: boolean;
};

export function useCarrierBooking({
	carrierPrice,
	onBookingStart,
	onBookingEnd,
	enableTenderOnBook = false,
	onTenderSuccess,
	onTenderError,
	onPaymentRequired,
}: UseCarrierBookingParams): UseCarrierBookingReturn {
	const { bookCarrier } = useBookCarrier();
	const { mutateAsync: updateCarriers } = useMutateCarriers();
	const { handleLtlActionError, refreshModal, isRefreshing } =
		useLtlActionErrorHandler();
	const { saveAdHocCarrier } = useSaveAdHocCarrier();
	const { showError } = useNotificationActions();
	const { isInternal } = useDeploymentType();
	const { quoteState, workflow, mergeResponseIntoQuoteState } =
		useContext(QuoteDetailsContext);
	const { submitBookTenderShipment } = useSubmitBookTenderShipment();
	const [isBooking, setIsBooking] = useState(false);

	// Blocks duplicate books fired before React commits isBooking, e.g. a held
	// Enter key repeating on the focused button.
	const inFlightRef = useRef(false);

	const sendCarrierBookedAnalytics = (carrierId: number) => {
		sendBookCarrierEvent(
			quoteState.id.toString(),
			carrierId.toString(),
			workflow,
		);
		ddBookCarrier(
			quoteState.id.toString(),
			carrierId.toString(),
			toDdWorkflow(workflow),
		);
	};

	// Book the carrier and tender the shipment in a single call. Used by the
	// LTL shipment carrier-selection flow (enableTenderOnBook).
	const bookAndTenderShipment = async (
		carrierToBook: CarrierPrice,
		bookETag: string,
	) => {
		const bookTenderResult = await submitBookTenderShipment({
			quoteId: quoteState.id,
			eTag: bookETag,
			selectedCarrierPriceId: carrierToBook.id,
		});

		if (bookTenderResult.status !== 200 || !bookTenderResult.data) {
			// Preserve the legacy "Refresh Quote" flow on eTag conflicts; surface
			// any other failure in the tender error modal.
			if (bookTenderResult.status === QUOTE_REFRESH_REQUIRED_STATUS) {
				handleLtlActionError(
					{
						success: false,
						status: bookTenderResult.status,
						error: bookTenderResult.message,
					},
					"tender",
				);
			} else {
				onTenderError?.(
					bookTenderResult.message ??
						"Failed to tender shipment. Please try again.",
				);
			}
			return;
		}

		sendCarrierBookedAnalytics(carrierToBook.id);

		// External pre-paid customers must pay (ACH/credit card) before the
		// shipment can finish tendering. The combined book + tender call books the
		// carrier and returns PaymentRequired in that case; hand off to the prepay
		// payment flow instead of jumping straight to the shipping-docs modal. This
		// mirrors the normal /shipment-details tender flow (see TermsOfService).
		if (
			!isInternal &&
			bookTenderResult.data.statusLevel === QuoteStatus.PaymentRequired
		) {
			onPaymentRequired?.(bookTenderResult.data);
			return;
		}

		onTenderSuccess?.(bookTenderResult.data);
	};

	const handleBookCarrier = async () => {
		onBookingStart?.();

		try {
			let carrierToBook = carrierPrice;
			let bookETag = quoteState.eTag;

			if (carrierPrice.isAdHoc && carrierPrice.id < 0) {
				const saveResult = await saveAdHocCarrier(
					carrierPrice,
					quoteState.id,
					quoteState.eTag,
				);

				if (!saveResult.success) {
					showError(
						"Failed to save custom carrier before booking. Please try again.",
					);
					return;
				}

				carrierToBook = saveResult.data.carrierPrice;
				bookETag = saveResult.data.eTag;
			}

			// Internal users can edit carrier price/margin inline, so we flush
			// the latest values via update-prices before select-carrier-price to
			// ensure the backend tenders the current rate. External users can't
			// edit these fields, so the extra round-trip is unnecessary.
			const formattedCarrier: UpdateCarrierPriceBody = {
				Id: carrierToBook.id,
				CarrierId: carrierToBook.carrierId,
				CarrierPriceTypeId: carrierToBook.carrierPriceTypeId,
				CarrierRate: carrierToBook.carrierRate,
				BrokerMargin: carrierToBook.brokerMargin,
				CarrierQuoteId: carrierToBook.carrierQuoteId ?? "",
				CarrierQuoteExpirationDate: carrierToBook.carrierQuoteExpirationDate,
				ServiceLevelId: carrierToBook.serviceLevel.id,
				TransitDays: carrierToBook.transitDays,
			};

			try {
				if (isInternal) {
					const updateResult = await updateCarriers({
						quoteId: quoteState.id,
						eTag: bookETag,
						carriers: [formattedCarrier],
					});

					if (!updateResult.success) {
						// Abort booking: proceeding with the stale eTag risks tendering
						// an out-of-date rate or surfacing a confusing 412 from the API.
						console.error(
							"Error updating carrier price before booking:",
							updateResult.error,
						);
						handleLtlActionError(updateResult, "book");
						return;
					}

					bookETag = updateResult.data.eTag;
					mergeResponseIntoQuoteState({
						id: updateResult.data.id,
						carrierPrices: updateResult.data.carrierPrices,
						eTag: updateResult.data.eTag,
					});
				}

				// When enableTenderOnBook is true (LTL shipment carrier-selection
				// flow), book the carrier and tender the shipment in a single call.
				if (enableTenderOnBook) {
					await bookAndTenderShipment(carrierToBook, bookETag);
					return;
				}

				const bookResult = await bookCarrier(carrierToBook.id, bookETag);

				if (!bookResult.success) {
					handleLtlActionError(bookResult, "book");
					return;
				}

				sendCarrierBookedAnalytics(carrierToBook.id);
			} catch (error) {
				// Catch unexpected errors (e.g. mergeResponseIntoQuoteState throwing)
				// so they don't surface as unhandled rejections.
				console.error("Error during carrier booking:", error);
				handleLtlActionError({ success: false });
			}
		} finally {
			onBookingEnd?.();
		}
	};

	const handleBookCarrierClick = () => {
		if (inFlightRef.current) return;
		inFlightRef.current = true;
		setIsBooking(true);
		void handleBookCarrier().finally(() => {
			inFlightRef.current = false;
			setIsBooking(false);
		});
	};

	return {
		handleBookCarrierClick,
		refreshModal,
		isRefreshing,
		isBooking,
	};
}

type SearchCarrierOptions = {
	onlyLTL?: boolean;
	excludeNoLoadCarriersForCustomerId?: number;
};

const getSearchCarrierRoute = (
	searchTerm: string,
	options?: SearchCarrierOptions,
) => {
	let route = apiRoutes.SEARCHFORCARRIERS.replace(
		":userInput",
		encodeURIComponent(searchTerm),
	);
	const queryParams: string[] = [];
	if (options?.onlyLTL !== undefined) {
		queryParams.push(`onlyLTL=${options.onlyLTL}`);
	}
	if (options?.excludeNoLoadCarriersForCustomerId !== undefined) {
		queryParams.push(
			`excludeNoLoadCarriersForCustomerId=${options.excludeNoLoadCarriersForCustomerId}`,
		);
	}
	if (queryParams.length > 0) {
		route += "&" + queryParams.join("&");
	}
	return route;
};

/**
 * Hook to manage carrier state and provide update functions
 * @returns Object containing carrier management functions and state
 */
export function useCarrierManagement() {
	const {
		removeBlankCarrier,
		quoteState,
		applyFoundCarrier,
		updateAdHocCarrier,
	} = useContext(QuoteDetailsContext);

	const customerId = quoteState.customer?.id ?? 0;
	const brokerId = quoteState.customer?.primaryBroker?.id ?? 0;

	return {
		removeBlankCarrier,
		quoteState,
		applyFoundCarrier,
		updateAdHocCarrier,
		customerId,
		brokerId,
		getSearchCarrierRoute,
	};
}
